// B.1.5 API client.
//
// request<T> returns {data, headers, requestId, vaultSyncFailed} on 2xx,
// throws ApiError on non-2xx. `vaultSyncFailed` flags a tenant POST that
// succeeded in Postgres but failed the Vault dual-write (HTTP 201 with
// `X-Vault-Sync: error` header — see backend/src/orixen_hub/api/v1/
// tenants.py). Tenant create UI uses this to keep the modal open and
// show a yellow VaultSyncBanner.
//
// Legacy `api` facade kept alive for /fleet's FleetPage; unwrap to .data
// internally so existing call sites compile unchanged.

import { ApiError, type ApiErrorJson } from "./errors";
import type {
  DeviceDetailResponse,
  HealthResponse,
  MeResponse,
  TenantDevicesResponse,
  TenantSummary,
} from "./types";

export interface ApiResponse<T> {
  data: T;
  headers: Headers;
  requestId: string | null;
  vaultSyncFailed: boolean;
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

let _bearerProvider: (() => string | undefined) | null = null;

/** Wire the auth context's access_token getter. Call from main.tsx. */
export function setBearerProvider(getter: () => string | undefined): void {
  _bearerProvider = getter;
}

// Shared send core: builds auth headers, fires the request, and throws ApiError
// on non-2xx. Callers (request<T> = JSON, requestText = raw text) decode the body
// themselves. `accept` lets non-JSON callers (artifact yaml, execution.log) ask
// for the right content type.
async function _send(
  method: Method,
  path: string,
  body: unknown,
  accept: string,
): Promise<{ response: Response; requestId: string | null; vaultSyncFailed: boolean }> {
  const headers: Record<string, string> = { Accept: accept };
  const bearer = _bearerProvider?.();
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  }
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    // Multipart: let the browser set Content-Type (incl. the boundary).
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const response = await fetch(path, { method, headers, body: payload });
  const requestId = response.headers.get("X-Request-Id");
  const vaultSyncFailed = response.headers.get("X-Vault-Sync") === "error";

  if (!response.ok) {
    const raw = await response.text();
    let parsed: ApiErrorJson | undefined;
    try {
      parsed = raw ? (JSON.parse(raw) as ApiErrorJson) : undefined;
    } catch {
      // Non-JSON body (probably a proxy / nginx HTML error page).
    }
    throw new ApiError({
      status: response.status,
      detail: parsed?.detail ?? raw ?? `${response.status} ${response.statusText}`,
      code: parsed?.code ?? _fallbackCode(response.status),
      resource: parsed?.resource ?? null,
      field: parsed?.field ?? null,
      requestId,
      raw: parsed ?? raw,
    });
  }

  return { response, requestId, vaultSyncFailed };
}

export async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const { response, requestId, vaultSyncFailed } = await _send(
    method,
    path,
    body,
    "application/json",
  );
  // 204 No Content has no body; treat as `undefined as T`.
  const data =
    response.status === 204
      ? (undefined as T)
      : ((await response.json()) as T);
  return { data, headers: response.headers, requestId, vaultSyncFailed };
}

export interface TextResponse {
  data: string;
  headers: Headers;
  requestId: string | null;
  vaultSyncFailed: boolean;
}

// Raw-text sibling of request<T>: same auth + ApiError handling, but returns the
// body verbatim instead of JSON-parsing it. Use for content-addressed render
// artifacts (application/yaml) and, in F2, execution.log / output artifacts that
// aren't JSON. Body type is requested via Accept: */*.
export async function requestText(
  method: Method,
  path: string,
  body?: unknown,
): Promise<TextResponse> {
  const { response, requestId, vaultSyncFailed } = await _send(method, path, body, "*/*");
  const data = response.status === 204 ? "" : await response.text();
  return { data, headers: response.headers, requestId, vaultSyncFailed };
}

function _fallbackCode(status: number): string {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status >= 500) return "server_error";
  return "error";
}

// ============================================================================
// Legacy `api` facade — kept for the /fleet view. Each method unwraps the
// envelope (`.data`) so legacy call sites stay synchronous-looking and the
// existing useQuery({ queryFn: api.tenants }) shape continues to compile.
// New code uses the typed wrappers in api/endpoints/*.ts (Tasks 4+).

async function _legacy<T>(method: Method, path: string): Promise<T> {
  const { data } = await request<T>(method, path);
  return data;
}

export const api = {
  health: () => _legacy<HealthResponse>("GET", "/healthz"),
  me: () => _legacy<MeResponse>("GET", "/api/v1/me"),
  tenants: () => _legacy<TenantSummary[]>("GET", "/api/v1/tenants"),
  tenantDevices: (slug: string) =>
    _legacy<TenantDevicesResponse>(
      "GET",
      `/api/v1/tenants/${encodeURIComponent(slug)}/devices`,
    ),
  deviceDetail: (slug: string, deviceId: number, hours: number = 24) =>
    _legacy<DeviceDetailResponse>(
      "GET",
      `/api/v1/tenants/${encodeURIComponent(slug)}/devices/${deviceId}?hours=${hours}`,
    ),
};
